import { Type } from '@angular/core';
import { FormControl, UntypedFormGroup } from '@angular/forms';
import { NzUploadFile, NzUploadXHRArgs } from 'ng-zorro-antd/upload';
import { BehaviorSubject, Observable, of, throwError } from 'rxjs';
import { catchError, tap } from 'rxjs/operators';
import { ResponseFromServer } from './response-from-server.model';

import { RxFormDealSubmitActionId } from '../../features/exchange-deals/_services/exchange-deals.config';
import { DealViewabilityFilter } from '../../features/exchange-deals/exchange-deal-form/_services/exchange-deal-form.config';
import { ImpType } from './impression-type';
import { RoleJava } from './role';
import { UniversalMediaList } from '@/app/features/universal-media-lists/models';
import { FilteringType } from '.';

export const featureToggleKeys = [
  'KeyValuePairTargeting',
  'dealExternalSync',
  'PaymentHistory',
  'SpearadLink',
  'consentCheckbox',
  'MaskDemoData',
  'HelpCenter',
  'dealExchangeExternalSync',
  'googleMarketplaceSync',
  'CuratedMarketplaceReporting',
  'ExchangeForecasts',
  'DealPartnerInternalInfo',
  'UniversalMediaLists',
  'AdomainReporting',
  'AudienceSegments',
  'ScheduledReports',
  'BarterFeeDealConfig',
  'ForecastWinningBidpriceStats',
  'AdvertibleRequestEnhancement',
  'NexAIChatbot',
  'DspAllMediaFilters',
  'KochavaMeasurement',
  'TTDSync',
  'JaversDealPackage',
  'TTDSyncPubDeals',
  'DspChildFilters',
  'PublisherQpsControls',
  'GlobalAllowlist'
] as const;
export type FeatureToggleKeys = typeof featureToggleKeys;
export type FeatureToggles = {
  [K in FeatureToggleKeys[number]]?: boolean
};
export type FeatureTogglesKey = keyof FeatureToggles;


const sharedStoreAssetKeys = [
  'featureToggles',
  'optionsLists',
  'combinedSelectOptions',
  'countries',
  'opsOwners',
  'samOwners',
  'bizdevOwners',
  'timezones',
  'currencies',
  'trafficSources',
  'trafficSourcesByPublisherId',
  'ssps',
  'iabCategories',
  'publisherForLayout',
  'dspAccountsGrouped',
] as const;
export type SharedStoreAssetKey = typeof sharedStoreAssetKeys[number];
export type SharedStoreAssetResponseStructureType<T> = ResponseFromServer<T | T[]> | T | T[];
export interface SharedStoreAssetConfig<T> {
  key: SharedStoreAssetKey;
  responseHandler?: (res: SharedStoreAssetResponseStructureType<T>, config: SharedStoreAssetConfig<T>) => any;
  responseIntegrityTest: (res: SharedStoreAssetResponseStructureType<T>) => boolean;
  isLoading: boolean;
  queueSubject: BehaviorSubject<Observable<SharedStoreAssetResponseStructureType<T>>>;
  response$: Observable<SharedStoreAssetResponseStructureType<T>>;
}

export interface PossibleValueForReportFilters {
  filterId: string;
  optionListKeys?: string[];
  combinedSelectOptions?: string[];
}

export class AppResponseHandler<T> {
  response$: Observable<T>;
  isLoading$: Observable<boolean>;
  httpError$: Observable<any>;
  private responseSubject: BehaviorSubject<T>;
  private isLoadingSubject: BehaviorSubject<boolean>;
  private httpErrorSubject: BehaviorSubject<any>;

  constructor(response: any = null, isLoading: boolean = false, httpError: any = null) {

    this.responseSubject = new BehaviorSubject<T>(response);
    this.response$ = this.responseSubject.asObservable();
    this.isLoadingSubject = new BehaviorSubject<boolean>(isLoading);
    this.isLoading$ = this.isLoadingSubject.asObservable();
    this.httpErrorSubject = new BehaviorSubject<any>(httpError);
    this.httpError$ = this.httpErrorSubject.asObservable();
  }

  onFetch(): void {
    this.isLoadingSubject.next(true);
    this.httpErrorSubject.next(null);
  }

  onSuccess(response: T): void {
    this.isLoadingSubject.next(false);
    this.responseSubject.next(response);
  }

  onError(error = throwError(new Error())): void {
    this.isLoadingSubject.next(false);
    this.httpErrorSubject.next(error);
  }

  start(obs$: Observable<T>): Observable<T> {
    this.onFetch();
    return obs$.pipe(
      catchError((err) => {
        this.onError(err);
        return of(null);
      }),
      tap((res: T) => {
        if (res) {
          this.onSuccess(res) ;
        }
      }),
    );

  }
}

export interface AuthPermissions {
  canRead?: boolean;
  canCreate?: boolean;
  canUpdate?: boolean;
  canDelete?: boolean;
  canView?: boolean;
  canUpdateStatus?: boolean;
  showActions?: boolean;
  alwaysShow?: boolean;
}

export interface AuthPermissionsCRUD {
  read?: string;
  create?: string;
  update?: string;
  delete?: string;
}

export interface OperationsOwner {
  email: string;
  id: number;
  name: string;
  userName: string;
  status: string;
  deletedAt: string;
}

export interface SamOwner {
  email: string;
  id: number;
  name: string;
  userName: string;
  status: string;
  deletedAt: string;
}

export class AppTable {
  id: string;
  pageIndex: number;
  pageSize: number;
  sortBy: string;
  sortDirection: string;
  hasPageSizeChanger?: boolean;
  isPaginated?: boolean;
  tableClass?: string;
  pageSizeOptions?: number[];
  initialSortDirection?: 'ASC' | 'DESC';
  expandRowComponent?: CustomComponentConfig;

  constructor(id: string,
    pageIndex: number = 1,
    pageSize: number = 10,
    sortBy: string = '',
    sortDirection: string = 'asc',
    hasPageSizeChanger: boolean = false,
    isPaginated: boolean = true,
    tableClass: string = '',
    pageSizeOptions: number[] = [10, 20, 30, 40],
    initialSortDirection?: 'ASC' | 'DESC',
    expandRowComponent = null
  ) {

    this.id = id;
    this.pageIndex = pageIndex;
    this.pageSize = pageSize;
    this.sortBy = sortBy;
    this.sortDirection = sortDirection;
    this.hasPageSizeChanger = hasPageSizeChanger;
    this.isPaginated = isPaginated;
    this.tableClass = tableClass;
    this.pageSizeOptions = pageSizeOptions;
    this.initialSortDirection = initialSortDirection ?? 'DESC';
    this.expandRowComponent = expandRowComponent;
  }
}

export interface CustomComponent {
  data: any;
}

export class CustomComponentConfig {
  constructor(
    public component: Type<any>,
    public data: any = {},
  ) {}
}

export interface ExpandableTableRow {
  isExpanded: boolean;
  isExpandDisabled: boolean;
  expandRowComponent?: CustomComponentConfig;
}

interface MoreTag {
  prop?: string;
  displayNameProp?: string;
  popoverTitle?: string;
  linked?: boolean;
  openTab?: boolean;
  additionalNameProperty?: string;
  noDataMessage?: string;
  includeFirstInList?: boolean;
  listItemFormatter?: (item: any) => string | {
    text: string;
    boldVariables: string[];
  };
  maxVisibleItems?: number;
}

export interface AppTableColumn {
  id: string | number;
  prop: string;
  displayName: string;
  type: string;
  isSortEnabled?: boolean;
  sortingProp?: string;
  actionId?: string;
  actions?: AppTableAction[];
  moreTag?: MoreTag;
  tooltip?: string;
  canBeHidden?: boolean;
  isHidden?: boolean;
  columnGroup?: string;
  isFrozenCol?: boolean;
  commentTag?: any;
  extraClass?: string;
  externalDisplayName?: string;
  maxWidth?: string;
  displayNameFormatter?: Function;
  valueFormatter?: Function;
  dataExtractionFn?: Function;
  data?: any;
  currency?: string;
  colWidth?: string;
  tags?: any[];
  toggleConfig?: {
    checked: (row: any) => boolean;
    disabled: boolean | ((row: any) => boolean);
  };
  tagConfig?: {
    checkFn?: (row: any) => boolean;
    tagClass: string | ((row: any) => string);
    tagText: string | ((row: any) => string);
    showTagAfterText?: boolean;
    textWidth?: string;
  };
  initialSortDirection?: 'ASC' | 'DESC';
}

export interface ColumnPreference {
  id: string | number;
  isHidden: boolean;
}

export interface AppTableAction {
  id: string | number;
  name: string;
  displayName: string;
  auth: string;
  desc?: string;
  icon?: string;
  class?: string;
  hrefLink?: string;
  hrefIdProp?: string;
  condition?: (row: any) => boolean;
}

export interface AppTableButton {
  id: string;
  type: string;
  title: string;
  hasLoadingSpinner: boolean;
  auth: string;
}

export class AppSelections {
  selectedItems: any[];
  excludedItems: any[];
  isSelectAll: boolean;
  changedItems?: any[];

  constructor(selectedItems: any[],
    excludedItems: any[],
    isSelectAll: boolean,
    changedItems: any[] = null) {
    this.selectedItems = selectedItems;
    this.excludedItems = excludedItems;
    this.isSelectAll = isSelectAll;
    this.changedItems = changedItems;
  }
}

export interface Publisher {
  accounting_id: number | string;
  badv: any;
  battr: number[];
  bcat: any;
  btype: number[];
  coppa: boolean | any;
  created_at: string;
  deleted_at: string;
  publisher_id: number | string;
  publisher_name: string;
  signup_step: string;
  status: string;
  tld_badv: any;
  updated_at: string;
  permissions?: any;
}
export type PublisherAccountType = 'MANAGED' | 'SELF_SERVE';

export interface ThinPublisher {
  publisherId: string | number;
  publisherName: string;
  deletedAt: Date;
  status: string;
  publisherTrafficSources: PublisherTrafficSource[];
  publisherAccountType: PublisherAccountType;
  operationsOwner: OperationsOwner;
  samOwner: SamOwner;
  accountingId: number | string;
  statusModificationDate?: string;
  deactivationReason?: string;
  disabled?: boolean;
}

export interface PublisherEdit {
  accountingId: number | string;
  zipId: number | string;
  zipErrorInfo?: string;
  adsTxtEmailing: boolean;
  badv: any;
  battr: any;
  bcat: any;
  btype: any;
  coppa: boolean;
  createdAt: string;
  ctvPreconsented: boolean;
  dataUsageRights: string;
  deletedAt: string;
  externalDomain: string;
  externalName: string;
  googlePubExId?: number;
  publisherFinancialDocument?: PublisherFinancialDocument [];
  permissions: Array<Permission>;
  pubInitiatedDealPlatformfee: number;
  publisherId: number;
  dealBidPriceType: string;
  publisherInfo: PublisherInfo;
  publisherName: string;
  publisherTermsOfServices: any;
  sellerTypes: Array<string>;

  publisherR1SellerId: string;

  bothR1SellerId: string;

  intermediaryR1SellerId: string;
  signupStep: 'NEW' | 'PENDING' | 'CONTENT_OK' | 'CONTENT_DECLINED' | 'ZIP_ERROR' | 'PAYMENT_HOLD' | 'PAYMENT_APPROVED';
  status: string;
  tldBadv: any;
  unrulyInitiatedDealPlatformfee: number;
  unrulyPlatformFee: number;
  updatedAt: string;
  statusModificationDate?: string;
  deactivationReason?: string;
  financial_documents?: PublisherFinancialDocument[];
  unrulySellerId?: string;
  dspAccountExcludeList?: (string | number)[];
  blockedCountries?: Array<{
    regionId: number;  // 1=US East, 2=US West, 3=EU West, 4=APAC
    countryId: number;
  }>;
  dealPartnerExcludeList?: (string | number)[];
}

export interface PublisherFinancialDocument {
  id: number;
  fileType: string;
  docType: string;
  createdAt: string;
  name: string;
}
export interface PublisherInfo {
  adServerTech: PublisherAdServerTech;
  adServerTechNotes: string;
  address1: string;
  address2: string;
  bizdevOwner: PublisherOwner;
  city: string;
  cogsType: string;
  taxId?: number;
  company: string;
  companyWebsite: string;
  contactEmail: string;
  contactFirstName: string;
  contactLastName: string;
  financeContactEmail: string;
  technicalContactEmail: string;
  country: string;
  createdAt: string;
  geoList: any;
  heardAboutFrom: any;
  integrationType: Array<string>;
  internalNotes: string;
  network: boolean;
  operationsOwner: PublisherOwner;
  samOwner: PublisherOwner;
  opportunityVolume: number;
  phone: string;
  propertyNotes: any;
  province: string;
  pubOpsCountry: string;
  pubCatList: any;
  publisherAccountType: string;
  publisherId: number;
  publisherMediaType: Array<string>;
  signupNotes: any;
  state: string;
  thirdPartyIntegration: boolean;
  updatedAt: string;
  zipCode: any;
}

export interface PublisherAdServerTech {
  name: string;
  id: number;
  displayName: string;
  configParameters: any;
}

export interface PublisherOwner {
  id: number;
  userName: string;
  name: string;
  email: string;
}

export interface KpiWidget<T = any> {
  key: T;
  value: {
    current: number;
    previous: number;
  };
  labels: {
    title: string;
    previous: string;
  };
  unit?: {
    left?: string;
    right?: string;
  };
  isLoading: boolean;
  decimals: number;
  isPreviousHidden?: boolean;
  extraData?: any;
  aggregationFunction?: AggregationFunction;
  requestConfig?: ApiServiceRequestConfig;
  minWidth?: number;
}

export interface EmailTemplate {
  content: string;
  createdAt: string;
  id: number;
  name: string;
  subject: string;
  updatedAt: string;
  wildcards: string;
}

export interface InventoryPackage {
  packageId?: number;
  publisher?: {
    publisherName?: string;
    publisherId?: number;
    permissions?: any;
  };
  publisherId?: number;
  deletedAt?: string;
  domainDealPackages: DomainBundle[];
  placements: number[];
  excludedPlacements: number[];
  selectAllPlacements: boolean;
  includeFuturePlacements: boolean;
  status: any;
  description: any;
  packageConfigType: PackageConfigType;
  notes: any;
  geoTargeting: GeoTargeting;
  filterGenre: any;
  filterGenreList: any;
  filterContentRating: any;
  filterContentRatingList: any;
  filterContentLivestream: ContentLivestream;
  filterContentLanguage: any;
  filterContentLanguageList: any;
  filterAdsize: null;
  filterAdsizeList: any;
  filterVideosize: null;
  filterVideosizeList: any;
  filterImpType: any;
  filterDeviceType: any;
  filterRewarded?: FilterRewarded;
  filterSite: boolean;
  filterApp: boolean;
  filterPreroll: boolean;
  filterMidroll: boolean;
  filterPostroll: boolean;
  filterCoppa: boolean;
  filterViewabilityPercent: any;
  filterCtrPercent: any;
  filterVcrPercent: any;
  debugEnd: any;
  publishers?: any;
  filterDomainBundle?: string;
  segmentGroups?: AudienceTargetingGroup[];
  maxDataFee?: number;
  numberOfUnsavedDomains: number;
  numberOfSavedDomains: number;
  kvpTargeting?: Array<KvpTargeting>;
}

export type DealConfigType = 'STANDARD' | 'SHARE_OF_VOICE' | 'BUNDLED';

export type PackageConfigType = 'STANDARD' | 'BVOD' | 'BVOD_FIRST_PARTY_DATA';

export enum FilterRewarded {
  REWARDED = 'REWARDED',
  NOT_REWARDED = 'NOT_REWARDED',
  ALL = 'ALL'
}

export type ExchangeDealFilterRewarded = 'all' | 'rewarded' | 'not_rewarded';

export interface Deal {
  dailyImpCap: any;
  revenueCap: any;
  dailyReqCap: any;
  dayParting: any;
  dealEnd: any;
  publisherDealPackageSovs: DealPackageSov[];
  // dealPackageId: any;
  dealStart: any;
  configType: DealConfigType;
  dealType: any;
  platformfeeType: any;
  customPlatformfee: number;
  customPlatformfeeType: string;
  adpodSlotTarget: string;
  deletedAt: any;
  description: any;
  externalDealId: any;
  bidfloorCurrency: string;
  notes: string;
  overallImpCap: number;
  frequencyCapDaily: number;
  frequencyCapMonthly: number;
  paceReqs: any;
  pubDealId: any;
  allowedToClone: boolean;
  status: any;
  updatedAt: any;
  buyers?: DealBuyer[];
  // dealPackageName?: string;
  publisherId?: number;
  iabCategoryParent?: any;
  auctionType?: string;
  isPublisherTargeted?: boolean;
  packagesLeftToSelect: boolean;
  bizdevOwner?: BizDevOwner;
  marketplaceMode: boolean;
  marketplaceDescription?: string;
  marketplaceLogoPath?: string;
  marketplaceRepresentedGroups?: UntypedFormGroup[];
  dealExternalSync?: DealExternalSync;
  // dealPackageDeletedAt?: string | number;
  bundledDealIds?: number[];
  paId?: number | null;
  applyPaDiscount?: boolean;
  padAgreement?: PadAgreement;
  ttdConfig?: TtdConfig | null;
}

export interface UploadFileMarketplaceLogo {
  uiAction: 'upload' | 'remove' | 'updated';
  file: NzUploadXHRArgs;
}

export interface DealPackageSov {
  // dealPackage: InventoryPackage,
  thinPublisherDealPackage: InventoryPackage;
  sovPercent: number;
}

export interface ExchangeDeal {
  name: string;
  dealId?: number;
  description: string;
  status: 'active' | 'inactive' | 'forecast_only';
  dealStart: string;
  dealEnd: string;
  externalDealId: string;
  type: string;
  officeCountry: string;
  bidfloorCurrency: string;
  brandId: number | null;
  campaign: string;
  agencyId: number | null;
  dealPartnerId: number | null;
  dealPartner?: Pick<DealPartner, 'id' | 'name'>;
  portfolioId: number | null;
  portfolio?: DealPortfolio;
  paId: number | null;
  applyPaDiscount: boolean;
  padAgreement?: PadAgreement;
  aviPaId: number | null;
  applyAviDiscount: boolean;
  aviAgreement?: PadAgreement;
  accountManagers: number[];
  adOps: number[];
  salesPersons: number[];
  dealPersonnels?: Personnel[];
  kpiTargetDailyRate: number | null;
  kpiTargetMonthlyRev: number | null;
  kpiTargetViewability: number | null;
  kpiTargetVcr: number | null;
  dailyReqCap: number | null;
  dailyImpCap: number | null;
  overallImpCap: number | null;
  supplyMarginPctEnabled: boolean;
  supplyMarginPct: number | null;
  barterFeePct: number | null;
  paceReqs: boolean;
  dspAccounts: DspAccountSharedAsset[];
  sspAccounts: ExchangeDealSsps[];
  viewabilityPercentage: number;
  deviceType: string[];
  wseat: string;
  wseatArray: string[];
  priorityId: number | null;
  auctionType: string;
  bidfloor: number | null;
  dataFee: number | null;
  demandMargin: DemandMargin;
  dealFloorAdjustment?: 'dedicated_budget' | '5' | '10' | '15' | '20' | null;
  inventoryType: string;
  filterSsp: string;
  filterSspIds: string[] | { id: number }[];
  ssps?: SupplySource[];
  sourceRatingTarget?: string | null;
  guaranteedVerification: string;
  impType: string;
  dealPubdealBundles?: PubDealBundle[] | number[];
  filterSite: boolean;
  filterApp: boolean;
  mediaType?: string[];
  requireContentTitle: boolean | 0 | 1;
  adType: string;
  filterVideoSkip: boolean | null | 0 | 1;
  filterVideoPlaybackMethod: number[];
  filterVideoMinContentLength: string | number | null;
  filterVideoMaxDurationMinimum: string | number | null;
  filterMinImpBidfloor: number | null;
  filterMaxImpBidfloor: number | null;
  filterOmsdk: boolean;
  filterVpaid: boolean;
  filterMraid: boolean;
  filterSimid: boolean;
  playThrough: boolean;
  filterPreroll: boolean;
  filterMidroll: boolean;
  filterPostroll: boolean;
  filterCoppa: boolean;
  filterDeviceType: any;
  dealPixels: DealPixel[];
  useKochava?: boolean | null;
  kochavaProductId?: string | null;
  notes: string;
  createdAt: string;
  updatedAt: string;
  filterAdsize: string | null;
  filterAdsizeList: string[];
  isIbv: boolean;
  impressionSubTypes: string[];
  filterVideosizeList: string[] | null;
  filterVideosize: string | null;
  filterSspDealIds: number[];
  sspDeals: SspDeals[];
  dealSegmentGroups?: AudienceTargetingGroup[];
  safetyFilters: { id: number }[];
  dealViewabilityFilters: DealViewabilityFilter[];
  filterCtrPercent: number;
  filterVcrPercent: number;
  filterContentLivestream: string;
  filterRewarded?: ExchangeDealFilterRewarded | null;
  marketplaceMode?: boolean;
  frequencyCapDaily?: number;
  frequencyCapMonthly?: number;
  geoTargeting?: GeoTargeting;
  dayParting: DayPartingValue[];
  demandMarginOpti: boolean;
  filterSchainMaxLength: number | null;
  ttdConfig?: TtdConfig | null;
}

export interface TtdConfig {
  sellerContactName: string | null;
  sellerContactEmail: string | null;
  buyerContactName?: string | null;
  buyerContactEmail?: string | null;
}

export type ThinExchangeDeal = Pick<ExchangeDeal, 'name' | 'dealId' | 'externalDealId' | 'dealEnd' | 'description' | 'status'>;

export interface DealPixel {
  dealId?: number;
  impressionUrl: string;
  createdAt?: Date;
  updatedAt?: Date;
}

export enum NestAuditAction {
  Create = 'CREATED',
  Update = 'UPDATED',
  Delete = 'DELETED',
}

export enum NestAuditEntityName {
  ExchangeDeal = 'deals',
  DspAccount = 'dsp-accounts',
  PadAgreement = 'pad-agreements',
  GlobalMediaAllowlist = 'global-media-allowlist',
}

export interface NestAudit {
  id: number;
  timestamp: Date;
  author: string | number;
  entityId: string | number;
  entityName: NestAuditEntityName;
  action: NestAuditAction;
  oldValue: object | null;
  newValue: object | null;
}

export interface ExchangeAuditTableRow {
  property: string;
  oldValue: unknown;
  newValue: unknown;
}

export interface ExchangeDealAction {
  action: string;
  id: string;
  row: ExchangeDeal;
}

export interface ExchangeDealForecastResult extends ExpandableTableRow {
  forecastId: number;
  dealId: number;
  fusionUserId: number;
  fusionUserName: string;
  dealConfig: object;
  result: ExchangeDealForecastResultResult;
  createdAt: Date;
  updatedAt: Date;
  notes?: string;
  totalRequests?: ExchangeDealForecastResultTotals;
}

export interface ExchangeDealForecastResultTotals {
  available: number;
  unavailable: number;
  total: number;
}

export interface ExchangeDealForecastResultResult {
  externalDealId: string;
  fileCount?: number;
  lineCount?: number;
  dataDate: string;
  dspAccounts: Map<string, ExchangeDealForecastResultEntity>;
  countries: Map<string, ExchangeDealForecastResultEntity>;
  elapsed_time?: number;
  userWarnings: string[];
  errors: string[];
  status: string;
}

export interface ExchangeDealForecastResultEntity {
  requests: number;
  attached: ExchangeDealForecastResultEntityAttached;
  notAttached: { [key: string]: number };
  expectedRequests: number;
  expected_lines: number;
  samplerate: number;
}

export interface ExchangeDealForecastResultEntityAttached {
  count: number;
  existingImpressions: number;
  topDomains: ExchangeDealForecastResultEntityTopDomain[];
  winningBidpriceStats?: WinningBidpriceStats;
}

export interface ExchangeDealForecastResultEntityTopDomain {
  name: string;
  count: number;
}

export interface ForecastMappedReason {
  key: string;
  label: string;
  percent: number;
  availReqs: number;
}

export interface WinningBidpriceStats {
  mean: number;
  min: number;
  max: number;
  median: number;
  count: number;
}

export interface DealBuyer {
  buyerType: string; // [ DIRECT, PREFERRED, STANDARD ]
  defaultBidfloor: any;
  dspAccount: any;
  pubDeal: any;
  pubDealBuyerId: any;
  status: any;
  wadomain: any;
  wseat: any;
  bcatExclusions: [];
  deletedAt?: any;
  useImpBidfloor?: any;
}

export interface IdNumber {
  id: number;
}

export interface IdAndName extends IdNumber {
  name: string;
}

export interface FilterSelectOption {
  displayName: string;
  id: string;
  value: string | number;
  isDisabled?: boolean;
}
export interface PossibleValueAppFilter {
  id: number | string;
  displayName: string;
  value: string | number;
  relatedValue?: number | string;
}

export interface GroupAppFilter {
  groupId: number;
  groupName: string;
  groupDisplayName: string;
  possibleValues: PossibleValueAppFilter[];
}

export interface UpdateAppFiltersRenderer {
  filterId: string;
  selectOptionId: string;
  hideCondition: boolean;
}

export type SortOption = 'asc' | 'desc';
export type SortDirectionType = 'DESC' | 'ASC' | null;

export enum SortDirection {
  ASC = 'ASC',
  DESC = 'DESC'
}

export class AppFilter {
  id: string;
  type: 'SELECT' | 'DATES_RANGE' | 'SEARCH' | 'SELECT_WITH_SWITCH' | 'SELECT_WITH_SORT' | string;
  prop: string;
  title: string;
  placeholder: string;
  selectMode?: 'default' | 'multiple' | 'tags';
  allowClear: boolean;
  isServerSearch?: boolean;
  serverSearchProps?: any;
  possibleValues?: any[];
  selectedValues?: any;
  searchText?: string;
  excludedValues?: any;
  isSelectAll?: boolean;
  isRequired?: boolean;
  defaultValues?: any;
  errorTooltip?: any;
  isDisabled?: any;
  isLoading?: boolean;
  showValueInLabel?: boolean;
  isValid?: boolean;
  isHidden?: boolean;
  gridArea?: string;
  isForApi?: boolean;
  sendParamAs?: 'payload' | 'query';
  customPayloadFormatter?: Function;
  customPayloadPath?: string;
  tooltip?: string;
  isReady?: boolean;
  isTitleHidden?: boolean;
  sortType: SortOption;
  groups?: GroupAppFilter[];
  limitFutureDateRange: Date;
  limitPastDateRange: Date;
  isInternalOnlyFilter?: boolean;
  featureToggleKey?: FeatureTogglesKey;

  constructor(id: string,
    type: string,
    prop: string,
    title: string,
    placeholder: string,
    selectMode: 'default' | 'multiple' | 'tags' = 'default',
    allowClear: boolean = true,
    isServerSearch: boolean = false,
    serverSearchProps: any = null,
    possibleValues: any[] = [],
    selectedValues: any = null,
    searchText: string = null,
    isSelectAll: boolean = false,
    excludedValues: any = null,
    isRequired: boolean = false,
    defaultValues: any = null,
    errorTooltip: string = '',
    isDisabled: boolean = false,
    isLoading: boolean = false,
    showValueInLabel: boolean = false,
    isValid: boolean = true,
    isHidden: boolean = false,
    gridArea: string = '',
    isForApi: boolean = true,
    sendParamsAs: 'payload' | 'query' = 'payload',
    customPayloadFormatter: Function = null,
    customPayloadPath: string = '',
    tooltip: string = null,
    isReady: boolean = true,
    isTitleHidden: boolean = false,
    sortType: SortOption = 'asc',
    groups: any[] = null,
    limitFutureDateRange = null,
    limitPastDateRange = null,
    isInternalOnlyFilter: boolean = false,
  ) {
    this.id = id;
    this.type = type;
    this.prop = prop;
    this.title = title;
    this.placeholder = placeholder;
    this.selectMode = selectMode;
    this.allowClear = allowClear;
    this.isServerSearch = isServerSearch;
    this.serverSearchProps = serverSearchProps;
    this.possibleValues = possibleValues;
    this.selectedValues = selectedValues;
    this.searchText = searchText;
    this.isSelectAll = isSelectAll;
    this.excludedValues = excludedValues;
    this.isRequired = isRequired;
    this.defaultValues = defaultValues;
    this.errorTooltip = errorTooltip;
    this.isDisabled = isDisabled;
    this.isLoading = isLoading;
    this.showValueInLabel = showValueInLabel;
    this.isValid = isValid;
    this.isHidden = isHidden;
    this.gridArea = gridArea;
    this.isForApi = isForApi;
    this.sendParamAs = sendParamsAs;
    this.customPayloadFormatter = customPayloadFormatter;
    this.customPayloadPath = customPayloadPath;
    this.tooltip = tooltip;
    this.isReady = isReady;
    this.isTitleHidden = isTitleHidden;
    if (this.type === 'SELECT_WITH_SORT') {
      this.sortType = sortType;
    }
    this.groups = groups;
    this.limitFutureDateRange = limitFutureDateRange;
    this.limitPastDateRange = limitPastDateRange;
    this.isInternalOnlyFilter = isInternalOnlyFilter;
  }
}

export class ReportingGroupByOption {
  id: number;
  displayName: string;
  value: string;
  relatedValue?: string;
  isInternalOnly?: boolean;
}

export class BillPayment {
  billPaymentId: number;
  vendorId: number;
  totalAmount: number;
  billPaymentDate: string;
  currency: string;
  bills: Bill[];
  period: string;
  voided: boolean;
}

export class Bill {
  amount: number;
  billId: number;
  billPaymentId: number;
  vendorId: number;
  billDate: string;
  voided: boolean;
  exchangeRate: number;
}

export class ModalConfig {
  constructor(type: string = 'basic') {
    this.type = type;
  }
  type: string;
}

export type WarningBoxType = 'basic' | 'extended';

export class WarningBoxConfig {
  type: WarningBoxType;
  title?: string;
  content?: string;
  extraData?: any;
  extraDataTitle?: string;

  constructor(type: WarningBoxType = 'basic', title?: string , content?: string, extraDataTitle?: string, extraData?: any) {
    this.type = type;
    this.title = title;
    this.content = content;
    this.extraDataTitle = extraDataTitle;
    this.extraData = extraData;
  }
}

export interface JapiTableFilter {
  fieldName: string;
  operation: 'EQUALS' | 'LIKE' | 'IN' | 'BETWEEN' | 'IS_NULL' | 'IS_NOT_NULL' | 'GREATER_THAN';
  value: number | string | boolean | any[];
}

export interface JapiQuery {
  filter?: {
    filters?: JapiTableFilter[];
    id?: number | string;
    withTrashed?: boolean;
  };
  paging?: {
    number: number;
    size: number;
  };
  sorting?: {
    sortDirection: SortDirectionType;
    sortingField: string;
  };
  include?: string[];
}

export type UserType = 'INTERNAL' | 'EXTERNAL' | 'API';

export enum UserManagementEntityType {
  PUBLISHER = 'PUBLISHER',
  EXCHANGE_PARTNER = 'EXCHANGE_PARTNER'
}
export type UserTypeLower = 'internal' | 'external';
export type DefaultUi = 'CTRL' | 'SPEARAD';


export interface KeycloakToken {
  access_token: string;
  refresh_token: string;
  expires_in: number;
  refresh_expires_in: number;
  expiresAt: number;
}

export interface KeycloakTokenDecoded {
  userId: number;
  userType: UserType;
  preferred_username: string;
  publisherId: number;
  partnerId: number;
  userRoles: string[];
  realm_access: {
    roles: string[];
  };
}

export interface KeycloakInfo {
  id: number;
  // userId: number;
  preferred_username: string;
  given_name: string;
  family_name: string;
  name: string;
  // type: UserType;
  userType: UserType;
  publisherId: number;
  partnerId: number;
  currencyId: number;
  timezoneId: number;
  defaultUi: DefaultUi;
  email_verified: boolean;
}

export interface CtrlUser {
  id: number;
  firstName: string;
  lastName: string;
  email: string;
  emailVerifiedAt: string;
  password: string;
  type: UserType;
  // userType: UserType;
  partnerId?: number;
  publisherId?: number;
  dealPartnerId?: number;
  timezone?: Timezone | number;
  currency?: Currency | number;
  defaultUi?: string;
  form?: UntypedFormGroup;
  toBeRemoved?: boolean;
  gatewayApiKey?: string;
  createdAt?: string;
  updatedAt?: string;
  deletedAt?: string;
}

export interface CtrlAuthorities {
  permissions?: string[];
  roles?: string[];
  portfolioIds?: number[];
}

export type CtrlUserAuthorities = CtrlUser & CtrlAuthorities;
export type CurrentUser = KeycloakToken & CtrlUserAuthorities;

export interface Permission {
  id: number;
  name: string;
  createdAt?: string;
  description?: string;
  updatedAt?: string;
}

export interface UserAccess {
  roleName: string | null;
  dealPartnerId: number | null;
  portfolioIds: number[];
}

export interface UserAccessForm {
  roleName: FormControl<string | null>;
  dealPartnerId: FormControl<number | null>;
  portfolioIds: FormControl<number[]>;
}

export interface UserWithRoles {
  user: CtrlUser;
  permissions: Permission[];
  roles: RoleJava[];
  portfolioIds?: number[];
}

export type UserPayloadFields = Partial<CtrlUser> & { passwordConfirmation?: string };

export interface UserWithRolesPayload {
  user: UserPayloadFields;
  roles?: Array<{ id: number }>;
  portfolioIds?: number[];
}

export interface ContractFile {
  contractFileId: number;
  contractFileName: string;
  contractFileCreatedAt: string;
  contractFileLoading: boolean;
}


export interface PublisherToEditModel {
  publisher: any;
}

export interface CampaignOverrideListItem {
  campaign: string;
  publisherId?: number;
  createdAt?: string;
  updatedAt?: string;
}

export interface ApiRequestDataBundle {
  queryParams?: {
    [key: string]: any;
  };
  payload?: any;
}

export interface PaymentMethodPaypal {
  paypal_email_enc: string;
}

export interface PaymentMethodAch {
  bank_name_enc: string;
  bank_aba_routing_enc: string;
  bank_account_beneficiary_enc: string;
  beneficiary_name_enc: string;
}

export interface PaymentMethodWire {
  bank_account_beneficiary_enc: string;
  swift_enc: string;
  iban_enc: string;
  intermediate_routing_enc: string;
  wire_bank_name_enc: string;
  wire_bank_account_number_enc: string;
}

export interface PaymentMethodPayoneer {
  payoneerEmailEnc: string;
}

export interface Timezone {
  abbreviation: string;
  alias: string;
  createdAt: string;
  description: string;
  gmtOffset: number;
  id: number;
  isSupported?: boolean;
  updatedAt: string;
  dropdownLabel?: string;
}

export interface Currency {
  abbreviation?: string;
  currencySymbol?: string;
  id: number;
  name?: string;
  isSupported?: boolean;
  createdAt?: string;
  updatedAt?: string;
}

export interface ExchangeRate {
  currency: string;
  rate: number;
}

export interface PlacementStatus {
  status: string;
}

export interface Placement {
  badv: any;
  badvList: string[];
  battr: any;
  bcat: any;
  btype: any;
  dspAccountExcludeList: any;
  cat: any;
  checksum: any;
  commissions: Commission[];
  coppa: boolean;
  createdAt: string;
  defaultAppName: any;
  defaultAppStoreUrl: any;
  defaultDomainBundle: any;
  deletedAt: string;
  domainChecksum: any;
  externalSourceId: any;
  filterDomainBundle: any;
  floors: Floor[];
  lda: boolean;
  mediaType: string;
  notes: string;
  placementId: number;
  placementName: string;
  pubTs: any;
  publishedAt: string;
  status: string;
  tldBadv: any;
  updatedAt: string;
  vpaidSupport: boolean;
  demandProfile: any;
  numberOfUnsavedDomains: number;
  numberOfSavedDomains: number;
  contentAttrRequestExclude: string | string[];
  contentAttrReportExclude: string | string[];
  cot?: string;
  advertibleNativeBanner?: boolean;
  advertibleNativeVideo?: boolean;
  advertibleVideo?: boolean;
  advertibleVideoRenderer?: AdvertibleVideoRenderer;
}

export enum AdvertibleVideoRenderer {
  ADVERTIBLE = 'ADVERTIBLE',
  PUBLISHER = 'PUBLISHER'
}

export type ThinPlacement = Pick<Placement, 'placementId' | 'placementName'>;

export interface Floor {
  bidFloor: number;
  createdAt?: string;
  floorAdjustment?: number;
  floorAdjustmentType?: 'PERCENT' | 'FIXED';
  id?: number;
  impType?: ImpType;
  placementId: number;
  publisherTrafficSourceId?: number;
  updatedAt?: string;
  country?: any;
  deviceType?: any;
  isEnabled?: boolean;
  isHidden?: boolean;
  supplyPlacementFloorId?: number;
  isGeoFloor?: boolean;
  curatedDealFloor?: number;
  internalDspFloor?: number;
  geoFloors?: any[];
}

export interface Commission {
  id: number;
  placementId: number;
  commissionType: string;
  commission: number;
  overridePubTsCommission?: boolean;
}

export interface CommissionToSubmit {
  isCommissionFormValid: boolean;
  placementCommissions: Commission[];
}

export interface DomainBundle {
  id: number;
  domainBundle: string;
  placementId?: number;
  packageId?: number;
  pubTsId?: number;
  includeSubDomains: boolean;
  createdAt?: string;
  updatedAt?: string;
  changes?: string;
}

export enum PublisherStatus {
  ACTIVE = 'ACTIVE',
  INACTIVE = 'INACTIVE',
  PENDING_ACTIVATION = 'PENDING_ACTIVATION',
}

export interface DomainBundlePayload {
  updatedDomainBundles: DomainBundle[];
  removedDomainBundles: DomainBundle[];
  addedDomainBundles: DomainBundle[];
  removeAllDomains: boolean;
}

export interface PadAgreementAdomainPayload {
  removedDomainBundles: string[];
  addedDomainBundles: string[];
  removeAllDomains: boolean;
}

export class RxSegmentMapPayload {
  updatedSegmentMaps: string[] = [];
  removedSegmentMaps: string[] = [];
  addedSegmentMaps: string[] = [];
  removeAllSegmentMaps: boolean;
  segmentDescription: string;
  existSegmentDescription: string;

  constructor(existSegmentName: string) {
    this.segmentDescription = existSegmentName;
    this.existSegmentDescription = existSegmentName;
  }

  updateSegmentDescription(segmentDescription: string): void {
    this.segmentDescription = segmentDescription;
  }

  updateDomainBundlePayload(domainBundlePayload: DomainBundlePayload): void {
    this.updatedSegmentMaps = domainBundlePayload.updatedDomainBundles?.map(d => d.domainBundle);
    this.removedSegmentMaps = domainBundlePayload.removedDomainBundles?.map(d => d.domainBundle);
    this.addedSegmentMaps = domainBundlePayload.addedDomainBundles?.map(d => d.domainBundle);
    this.removeAllSegmentMaps = domainBundlePayload.removeAllDomains;
  }

  isChanged(): boolean {
    return this.segmentDescription !== this.existSegmentDescription || this.removeAllSegmentMaps ||
      this.updatedSegmentMaps.length > 0 || this.removedSegmentMaps.length > 0 || this.addedSegmentMaps.length > 0;
  }

  isNotChanged(): boolean {
    return !this.isChanged();
  }
}

export interface TrafficSource {
  accountingId: number;
  allowTmaxOverride: boolean;
  clickMacro: any;
  createdAt: string;
  defaultPlacementId: number;
  deletedAt: string;
  displayName: string;
  rxSspName: string;
  shortName: string;
  status: string;
  tmax: number;
  trafficSourceId: number;
  updatedAt: string;
  visible?: boolean;
  supportBvod: boolean;
  trafficSourceFeePct?: number;
  tsFeeReducesBid?: boolean;
}

export interface PublisherTrafficSourceCommission {
  commission: number;
  commissionType: 'FIXED' | 'REVSHARE_DYNAMIC' | 'REVSHARE_MINIMUM' | 'REVSHARE_STRICT';
  createdAt: string;
  id: number;
  placementId: number;
  publisherTrafficSourceId: number;
  updatedAt: string;
}

export interface PublisherTrafficSourceRegionLimit {
  pubTsId: number;
  regionIdentifier: string;
  regionName?: string;
  qpsLimit: number;
  qpsDefined: boolean;
}

export interface RegionLimitMetadata {
  pubTsId: number;
  regionsMaxQps: Map<string, number>;
}

export interface Last24HoursQps {
  region: string;
  last24HoursQps: number;
}

export interface InsightsMetricsResponse<T> {
  startDate: Date;
  endDate: Date;
  currency?: string;
  publisherId?: number;
  publisherName?: string;
  totalElements?: number;
  maxTotalElement?: number;
  fileName?: string;
  timezone?: string;
  metrics?: T[];
}

export interface InsightsMetricsRequest {
  filters?: Record<string, unknown>;
  groupBy?: string[];
  paging?: {
    number: number;
    size: number;
  };
  sorting?: {
    column: string;
    direction: 'ASC' | 'DESC';
  };
  timezone?: string;
  currency?: string;
  isInternal?: boolean;
  startDate?: Date;
  endDate?: Date;
  dateInterval?: string;
}


export interface PublisherTrafficSource {
  commissions: PublisherTrafficSourceCommission[];
  createdAt?: string;
  defaultBundle: any;
  demandProfile: string;
  filterDomainBundle: string;
  trafficSourceId?: number;
  trafficSource?: TrafficSource;
  floors: Floor[];
  pubTsId: number;
  status: string;
  updatedAt?: string;
  visible: boolean;
  uploadedFile?: NzUploadFile[];
  domainBundlePayload?: DomainBundlePayload;
  numberOfUnsavedDomains: number;
  numberOfSavedDomains: number;
  market: string;
  sellerType: string;
  contentAttrRequestExclude: string | string[];
  contentAttrReportExclude: string | string[];
  trafficSourceName?: string;
  cot?: string;
  qpsLimitRegions: PublisherTrafficSourceRegionLimit[];
  tsFeeOverride?: boolean;
  tsFeePctOverride?: number;
}

export interface DomainFilter {
  isDomainFilterValid: boolean;
  filterDomainBundle: string;
  domainsCount: number;
  isDomainsLoading: boolean;
}

export interface AudienceTargetingGroup {
  id: number;
  name: string;
  include: boolean;
  segments: AudienceTargetingSegment[];
  isOpen?: boolean;
}

export interface AudienceTargetingSegment {
  segmentId: number | undefined;
  sourceSegmentId?: number;
  segmentName: string;
  description?: string;
  providerId?: number;
  providerName?: string;
  cpm?: number;
  publisherId?: number;
  status?: StatusActivity;
  dmpAudienceId?: number;
  type?: string;
  segmentTypeName?: string;
  segmentAudienceDisabled?: boolean;
  segmentMaps?: string[];
  universalMediaList?: UniversalMediaList;
}

export interface AudienceTargetingProviderIdToName { [providerId: number | string]: string }

export class KvpTargeting {
  filterType: string;
  publisherKvp: PublisherKvp;
  possibleValues?: Array<PublisherKvpValue>;

  constructor(filterType: string, publisherKvp: PublisherKvp, possibleValues: Array<PublisherKvpValue> = []) {
    this.filterType = filterType;
    this.publisherKvp = publisherKvp;
    this.possibleValues = possibleValues;
  }
}

export class PublisherKvp {
  publisherKvpKey: PublisherKvpKey;
  publisherKvpValues: Array<PublisherKvpValue>;

  constructor(publisherKvpKey: PublisherKvpKey, publisherKvpValues: Array<PublisherKvpValue>) {
    this.publisherKvpKey = publisherKvpKey;
    this.publisherKvpValues = publisherKvpValues;
  }
}

export class PublisherKvpKey {
  id: number;
  kvpKey: string;
  displayName: string;
  updatedAt: string;
  createdAt: string;
  constructor(id: number = null, kvpKey: string = 'Key', displayName: string = null) {
    this.id = id;
    this.kvpKey = kvpKey;
    this.displayName = displayName;
  }
}

export class PublisherKvpValue {
  id: number;
  kvpKeyId: number;
  kvpValue: string;
  displayName: string;
  updatedAt: string;
  createdAt: string;
  deletedAt: string;
  constructor(kvpValue: string) {
    this.kvpValue = kvpValue;
  }
}

export class PublisherKvpValueResponse {
  publisherKvpValuesDtos: PublisherKvpValue[];
  createdValuesQty: number;
  existingValuesQty: number;
  invalidValuesQty: number;
  duplicateValuesQty: number;
  errMessage: string;
}

export interface ContentGenre {
  attribute: string;
  createdAt: string;
  id: number;
  updatedAt: string;
  value: string;
}

export enum SegmentType {
  mediadomain = 'Media Domain',
  mediaid = 'Media ID',
  pubid = 'Publisher ID'
}

export enum WindowMessageType {
  DATA_POINTS_REQUEST = 'DATA_POINTS_REQUEST',
  DATA_POINTS = 'DATA_POINTS',
  RESULT = 'RESULT',
  ACK = 'ACK',
  TEST = 'TEST'
}

export enum WindowMessageAckType {
  DATA_POINTS = 'DATA_POINTS',
  RESULT = 'RESULT',
  TEST_NEXT = 'TEST_NEXT'
}

export class GoogleCTASession {
  token: string;
  originProduct: string;
  googlePubId: string;
  redirectUrl: string;
  networkCode: string;
}

export enum GoogleCTATestDirective {
  START = 'START',
  NEXT = 'NEXT'
}

export enum GoogleCtaResultType {
  SUCCESS = 'SUCCESS',
  CANCEL = 'CANCEL',
  OTHER = 'OTHER',
  ERROR = 'ERROR'
}

export interface GoogleCtaResultOptions {
  'keep-window-open'?: boolean;
  'result-details'?: string;
}

export interface PopupExternal {
  id: string;
  localStorageKey: string;
  isVisible: boolean;
}

export interface KeyValueTitle<T = string> {
  title: string;
  value: string;
  key: T;
}

export interface KeyValueEnabled {
  title: string;
  value: string;
  enabled: boolean;
}

export interface KeyValuesType {
  id: number;
  type: string;
}

export interface KeyValue {
  id?: number;
  key?: string;
  value?: string;
}

export interface BizDevOwner {
  createdAt: string;
  deletedAt: string;
  email: string;
  id: number;
  name: string;
  status: string;
  updatedAt: string;
  userName: string;
}

export interface RejectReason {
  text: string;
  value: number | string;
  obj?: EmailTemplate;
}
export interface ApprovalQueueEmailDetails {
  customEmailText: string;
  templateComposerEnum: string;
  toSendEmail: boolean;
}

export enum ApprovalQueueButtonType {
  approveIcon = 'APPROVE_ICON',
  approvedBtn = 'APPROVED_BUTTON',
  saveBtn = 'SAVE_BUTTON',
}

export type CtrlNavigationContext = 'ctrl' | 'spearad' | 'supply' | 'partner' | 'exchange';

export interface CtrlNavigationEntry {
  id: string;
  label: string;
  path: string[];
  iconLayers?: FontAwesomeIconLayer[];
  lottieFile?: string;
  children?: CtrlNavigationEntry[];
  isSelected?: boolean;
  showChildren?: boolean;
  iframe?: boolean;
  rendererAuthConfig?: CtrlNavigationEntryRendererAuthConfig;
  rendererFn?: (obj: CtrlNavigationEntry, context: CtrlNavigationContext) => boolean;
  publisherRendererFn?: (publisher: PublisherEdit) => boolean;
  actionId?: string;
  actionPayload?: { context?: CtrlNavigationContext };
  position?: 'top'|'bottom';
  class?: string;
  tooltip?: string;
  disabled?: boolean;
  featureToggleKey?: FeatureTogglesKey;
}

export interface CtrlNavigationEntryRendererAuthConfig {
  userPermissions?: {
    permissions: string[];
    operand: 'AND'|'OR';
  };
  excludedUserRoles?: string[];
  grantingUserRoles?: string[];
}

export interface Breadcrumb {
  label: string;
  url: string;
  queryParams?: string;
}

export interface LastPath {
  ctrl?: string;
  spearad?: string;
  supply?: string;
  exchange?: string;
}

export interface ISAMenuEntry {
  caption?: string;
  icon?: string;
  path: string;
  children?: ISAMenuEntry[];
  routerPath?: string[];
}
export interface PublisherType {
  id: string;
  value: string;
  label: string;
  // disabled - configuration for external user
  disabled?: boolean;
}

export interface CountryPhoneCode {
  id?: string;
  countryCode: string;
  phoneLength?: number[];
  iso?: string;
  validLengths?: number[];
}
export interface Country {
  id: number;
  name: string;
  code: string;
  alpha2: string;
  alpha3: string;
  countryGroups?: any;
  globallyBlocked?: boolean;
  legallyBlocked?: boolean;
}

export interface State {
  regionKey: number;
  country: string;
  region: string;
  displayRegion: string;
}

export interface CountryToRegionsMap {
  [key: string]: State[];
}

export interface FontAwesomeIconLayer {
  icon: string;
  type?: 'fas'|'far';
  transform?: string;
  style?: string;
  spin?: boolean;
}

export interface AdServerTech {
  id: number;
  name: string;
  displayName: string;
}

export interface AssetsBlockedLists {
  iabCategories: any[];
  iabBannerAdTypes: IabBat[];
  iabCreativeAttributes: IabAttribute[];
  dspAccounts: DspAccountsContainer;
  dealPartners: DealPartner[];
}

export type ModalActionType = 'CANCEL' | 'DELETE' | 'APPROVE' | 'OK';

export interface ModalButton {
  type: ModalActionType;
  text: string;
  addClass?: string;
}

export interface BasicModal {
  id?: string;
  title?: string;
  buttons: ModalButton[];
}

export interface IabCategoryParent{
  key: string;
  title?: string;
  children?: IabCategoryChild[];
}

export interface IabCategoryChild {
  key?: string;
  title?: string;
  isLeaf?: boolean;
}

export interface IabBat {
  adTypeId: number;
  description: string;
}

export interface IabAttribute {
  attributeId: number;
  description: string;
}

export interface DspAccountFee {
  screenType: 'ctv' | 'app' | 'site';
  pmpPercent: number;
  ompPercent: number;
}

export interface DspChild {
  id: number;
  dspAccountId: number;
  name: string;
  nameDisplay?: string;
  status: StatusActivity;
  filterPubId?: FilteringType;
  filterSiteId?: FilteringType;
  filterAppId?: FilteringType;
  filterAppBundle?: FilteringType;
  filterDomain?: FilteringType;
}

export interface DspAccountSharedAsset {
  id: number;
  shortName?: string;
  nameDisplay?: string;
  externalName?: string;
  pubDealSupport?: boolean;
  status?: StatusActivity;
  dspAccountFees?: DspAccountFee[];
  displayLabel?: string;
}

export interface DspAccount extends DspAccountSharedAsset {
  filterPubId?: FilteringType;
  filterAppId?: FilteringType;
  filterSiteId?: FilteringType;
  filterAppBundle?: FilteringType;
  filterDomain?: FilteringType;
  childDsps?: DspChild[];
}

export interface DspAccountsContainer {
  activeDspAccountList: DspAccountSharedAsset[];
  inactiveDspAccountList: DspAccountSharedAsset[];
}

export interface Audit {
  timestamp: string;
  username: string;
  fieldName: string;
  parent: string;
  parentName?: string;
  actionType: string;
  previousValue: any;
  newValue: any;
  preserveValueCase?: boolean;
}

export interface Forecast extends ExpandableTableRow {
  forecastId: number;
  packageId: number;
  dspAccountName: string;
  result: ForecastResult;
  config?: ForecastConfig;
  createdAt: string;
}

export interface ForecastRunResponse {
  forecastId: number;
  result: ForecastResult;
}

export interface ForecastResult {
  errors: string[];
  status: 'success' | 'failed';
  dataDate: string;
  elapsedMS: number;
  fileCount: number;
  lineCount: number;
  userWarnings: string[];
  dspAccountName: string;
  realSampleRate: number;
  confidenceScore: number;
  availabilityRate: number;
  lowConfidenceWarning: number;
  package: ForecastResultPackage;
}

export interface ForecastResultPackage {
  requests: number;
  available: ForecastResultPackageAvailable;
  notAvailableReasons: ForecastResultPackageNotAvailableReasons;
}

export interface ForecastResultPackageAvailable {
  sampleCount: number;
  availableReqs: number;
  existingFillRate: number;
  existingImpressions: number;
  existingImpressionPubCost: number;
  existingImpressionPubCostAvg: number;
}

export interface ForecastResultPackageNotAvailableReasons {
  dma?: number;
  genre?: number;
  region?: number;
  country?: number;
  zip_code?: number;
  placement?: number;
  ctr_toolow?: number;
  vcr_toolow?: number;
  ctr_missing?: number;
  device_type?: number;
  vcr_missing?: number;
  domainbundle?: number;
  imp_sub_type?: number;
  contentrating?: number;
  content_network?: number;
  content_channel?: number;
  kvp_content_network_include?: number;
  kvp_content_channel_include?: number;
  kvp_content_series_include?: number;
  kvp_content_title_include?: number;
  kvp_customkey1_include?: number;
  kvp_customkey2_include?: number;
  kvp_customkey3_include?: number;
  kvp_customkey4_include?: number;
  kvp_customkey5_include?: number;
  kvp_content_network_exclude?: number;
  kvp_content_channel_exclude?: number;
  kvp_content_series_exclude?: number;
  kvp_content_title_exclude?: number;
  kvp_customkey1_exclude?: number;
  kvp_customkey2_exclude?: number;
  kvp_customkey3_exclude?: number;
  kvp_customkey4_exclude?: number;
  kvp_customkey5_exclude?: number;
  customkey1?: number;
  customkey2?: number;
  customkey3?: number;
  customkey4?: number;
  customkey5?: number;
  audience_exclude?: number;
  audience_include?: number;
  content_language?: number;
  viewability_toolow?: number;
  viewability_missing?: number;
  content_title_missing?: number;
}

export interface ForecastConfig {
  package: ForecastConfigPackage;
  dataDate: string;
  dspAccountName: string;
}

export interface ForecastConfigPackage {
  audienceTargetingGroups?: any[];
  contentLanguage?: ClusivityType;
  contentLanguageList?: string[];
  contentChannel?: ClusivityType;
  contentChannelList?: string[];
  contentNetwork?: ClusivityType;
  contentNetworkList?: string[];
  contentrating?: ClusivityType;
  kvpTargeting?: Map<string, ForecastConfigPackageKvpsValues>;
  contentratingList?: string[];
  country?: ClusivityType;
  countryList?: string[];
  fullCountryList?: Country[];
  deviceTypeTarget?: string[];
  dma?: ClusivityType;
  dmaList?: string[];
  domainBundle?: string;
  domainBundleList?: string[];
  genre?: ClusivityType;
  genreList?: string[];
  impType?: string | ImpType; // TODO: remove "string" type
  packageID?: number;
  placementList?: string[];
  publisherID?: number;
  region?: ClusivityType;
  regionList?: string[];
  requireContentTitle?: number;
  timestamp?: number;
  vcrPercent?: number;
  viewabilityPercent?: number;
  zipCode?: ClusivityType;
  zipCodeList?: string[];
}

export interface ForecastConfigPackageKvpsValues {
  values: string[];
  filterType: ClusivityType;
  displayName: string;
}

export type ClusivityType = 'include' | 'exclude';

export type ForecastFilterConfigType = 'table' | 'filter' | 'singleValue' | 'multiValue';

export type ForecastFilterConfigDataFormatter = (config: ForecastFilterConfig) => any;

export type ForecastFilterConfigValueFormatter = (val: any, config: ForecastFilterConfig) => any;

export interface ForecastFilterConfigTableColumn {
  key: string;
  label: string;
  cellFormatter?: ForecastFilterConfigValueFormatter;
}

export type ForecastFilterConfigTitleFormatter = (config: ForecastFilterConfig) => string;

export interface ForecastFilterConfig {
  type: ForecastFilterConfigType;
  dataKey: string;
  data: any;
  dataFormatter?: ForecastFilterConfigDataFormatter;
  clusivityKey?: string;
  clusivity?: ClusivityType;
  title?: string;
  titleFormatter?: ForecastFilterConfigTitleFormatter;
  assets?: any;
  assetsStoreSelector?: string;
  assetsServiceGetter?: string;
  assetsServiceGetterArgs?: string[];
  valueFormatter?: ForecastFilterConfigValueFormatter;
  tableColumns?: ForecastFilterConfigTableColumn[];
}

export type ModeType = 'edit' | 'read';

export enum ContentLivestream {
  LIVE = 'Live Stream',
  NOT_LIVE = 'VOD',
}

export interface DropdownButton {
  id: string | RxFormDealSubmitActionId;
  label: string;
  disabled?: boolean;
  extraClass?: string;
}

export enum ReportFilterButtonActions {
  GENERATE = 'generate',
  DOWNLOAD = 'download'
}

export enum ReportType {
  PUBLISHER = 'publisher',
  SUPPLY = 'supply',
  DEALS = 'deals',
  DEMAND = 'demand',
  DETAILED = 'detailed',
  QPS = 'qps',
  CURATED_MARKETPLACE = 'curatedMarketplace',
  ADOMAIN = 'adomain'
}


export interface MainDropdownButton extends DropdownButton {
  buttonType: 'primary-button' | 'secondary-button';
}

export interface DropdownButtonsConfig {
  main: MainDropdownButton;
  options: DropdownButton[];
}

export type SanitizerType = 'html' | 'style' | 'script' | 'url' | 'resourceUrl';

export interface AppChartResult {
  name: string;
  value: number;
}

export interface AppChartSeries {
  name: string;
  series: AppChartResult[];
  nameForChartTooltip?: string;
  isVisibleInLegend?: boolean;
  legendTooltipTitle?: string;
}

export type AppChartType = 'line' | 'barVertical' | 'barHorizontal' | 'pie' | 'gauge' | 'table';

export interface AppChartConfig<T = any> {
  key: T;
  type: AppChartType;
  data: AppChartResult[] | AppChartSeries[];
  isLoading: boolean;
  size?: AppChartSizeConfig;
  xAxis?: AppChartAxis;
  yAxis?: AppChartAxis;
  autoScale?: boolean;
  showLegend?: boolean;
  timeline?: boolean;
  title?: string;
  customColors?: { name: string; value: string; dashed?: boolean; hollow?: boolean }[];
  extraData?: any;
  extraClass?: string;
  requestConfig?: ApiServiceRequestConfig;
  requiredPermissions?: string[];
}

export interface AppChartSizeConfig {
  aspectRatio?: number;
  minHeight?: number;
  maxHeight?: number;
}

export interface AppChartAxis {
  showLabel?: boolean;
  label?: string;
  ticks?: string[] | number[];
  tickFormat?: AppChartTickFormat;
}

export type AppChartTickFormat = (val: any, config: AppChartConfig) => string;

export interface ApiServiceRequestConfig extends ApiRequestDataBundle {
  apiServiceMethod: string;
  methodArgumentPaths?: string[];
  methodArguments?: any[];
  preRequestHandler?: Function;
  responseHandler?: Function;
  expiresAt?: number;
}

export interface ContentNetwork {
  id: number;
  network: string;
  publisherId: number;
  networkDisplayName: string;
}

export interface ContentChannel {
  id: number;
  channel: number;
  publisherId: number;
  channelDisplayName: string;
}

export type PercentageOrFixed = 'percentage' | 'fixed';

export interface ExchangeDealPersonnel {
  data:  Personnel[];
}

export interface ExchangeDealDspAccounts {
  id: number;
  name?: string;
}

export interface ExchangeDealSsps {
  id: number;
  name: string;
}

export interface GeoTargeting {
  countries?: (string | number)[];
  regions?: string[];
  metros?: (string | number)[];
  zipCodes?: string[];
  filterCountry?: ClusivityType | string;
  filterRegion?: ClusivityType | string;
  filterDma?: ClusivityType | string;
  filterZipCode?: ClusivityType | string;
}

export interface GeoTargetingFilterList {
  nameProp: string;
  apiProp: string | number;
  queryProp: string | number;
}

export interface SspDealList {
  sspDealId: number;
  sspId: number;
  nameDisplay: string;
  externalDealId: string;
}
export interface SSP {
  id: number;
  name: string;
  nameDisplay: string;
}

export interface SspDealsGroup {
  groupName: string;
  elements: SspDealList[];
}

export interface ExchangeDealsMeta {
  totalItems: number;
  itemCount: number;
  itemsPerPage: number;
}


export interface Personnel {
  dealPersonnelId?: number;
  id?: number;
  role?: string;
  name?: string;
  status?: string;
}

export interface DeviceType {
  ANY: string;
  CONNECTEDTV: string;
  CTV: string;
  MOBILE: string;
  PHONE: string;
  TABLET: string;
  DESKTOP: string;
  SETTOPBOX: string;
}

export interface DealAgency {
  id?: number;
  name: string;
  status: string;
}

export interface SupplySource {
  id: number;
  name?: string;
  nameDisplay?: string;
  isInternal?: boolean;
}

export interface Campaign {
  campaign: string;
}

export interface OptionsLists {
  [enumKey: string]: { [optionKey: string]: string };
}

export interface OptionsList {
  DeviceType: DeviceType;
}

export interface Metro {
  metroKey: number;
  country: string;
  regions: string;
  metroName: string;
  displayMetroName: string;
}

export interface ServerError {
  error: {
    error: string;
    ststusCode: number;
    message: string[];
  };
}

export interface SelectIcon {
  id?: number;
}

export interface DealBrand {
  id?: number;
  name: string;
  status: string;
  createdAt?: string;
  updatedAt?: string;
}

export type GenericForm<T> = {
  [K in keyof T]?: FormControl<T[K] | null>;
};

export enum AgreementType {
  PMP = 'PMP',
  PUBPMP = 'PUBPMP',
  OMP = 'OMP',
  AVI = 'AVI',
}
export interface PadAgreementSeat {
  paId?: number;
  dspAccountId: number;
  seats: string[];
  createdAt?: string;
  updatedAt?: string;
}
export interface PadAgreementTier {
  id?: number;
  paId: number;
  spendThreshold?: number;
  discountPercent?: number;
  ctvDiscountPercent?: number;
  displayDiscountPercent?: number;
  olvDiscountPercent?: number;
  createdAt?: string;
  updatedAt?: string;
}

export interface PadAgreementMinimumSupplyMargin {
  ctvMinMargin: number;
  displayMinMargin: number;
  olvMinMargin: number;
}

export interface PadAgreement {
  paId?: number;
  agreementName?: string;
  agreementType?: AgreementType;
  includePubdealSpend?: boolean;
  startDate?: string;
  endDate?: string;
  isExpired?: boolean;
  currentSpend?: number;
  currentTier?: number;
  notes?: string;
  padAgreementSeats: PadAgreementSeat[];
  padAgreementTiers: PadAgreementTier[];
  createdAt?: Date;
  updatedAt?: Date;
  padAgreementDomains?: PadAgreementAdomainPayload;
  ctvMinMargin?: number;
  displayMinMargin?: number;
  olvMinMargin?: number;
}

export interface DomainBundleDownloadParams {
  parentType: string;
  id: number;
  name?: string;
  tsId?: number;
  segmentId?: number;
}

export class DealType {
  id: number;
  type: string;
  status: string;
  allowMarginOpti?: boolean;
}

export class DemandMargin {
  demandMarginPct: number;
  demandMarginFixed: number;
  demandMarginsByPublisher?: DemandMarginByPublisher[];
}

export class DemandMarginByPublisher {
  id: number;
  publisherId: number;
  publisherName: string;
  demandMarginPct: number;
}

export interface SafetyFilterProvider {
  id: number;
  provider: string;
  name: string;
  scoreThreshold?: number;
}

export enum PartnerType {
  CURATOR = 'CURATOR',
  AGENCY = 'AGENCY'
}

export interface DealPartner {
  id: number | string;
  name: string;
  status: string;
  createdAt?: string;
  updatedAt?: string;
  deletedAt?: string;
  audienceProviders?: DmpAudienceProvider[];
  platformFee?: number;
  platformFeeType?: PlatformFeeEnum;
  defaultDemandMargin?: number;
  notes?: string;
  permissions?: Array<Permission>;
  partnerType: PartnerType;
  parentPartnerId?: number | null;
  parentPartnerName?: string;
  hasChildPartners?: boolean;
  isHoldCo?: boolean;
}

/**
 * A selectable partner: the dropdown needs an id and a label, not a whole DealPartner. Derived from
 * the model so renaming either field cannot leave this shape behind.
 */
export type DealPartnerOption = Pick<DealPartner, 'id' | 'name'>;

export interface Portfolio {
  id: number;
  name: string;
  partnerId: number;
}

/** rx-deals-api's view of a portfolio, already narrowed to what the caller may see. */
export interface DealPortfolio {
  id: number;
  name: string;
  dealPartnerId: number;
}

export interface PortfolioScopeResponse {
  portfolioIds: number[];
}

export interface AdSize {
  sort: number;
  adSize: string;
}

export interface DmpAudienceProvider {
  providerId: number;
  providerName: string;
  sourceDmp: string;
}



export interface CombinedSelectOptions {
  dspAccounts: { activeDspAccountList: DspAccountSharedAsset[]; inactiveDspAccountList: DspAccountSharedAsset[] };
  dealAgencies?: DealAgency[];
  dealBrands?: DealBrand[];
  adSizes?: AdSize[];
  supplySources?:  { activeSspList: SupplySource[]; inActiveSspList: SupplySource[] };
  dealPersonnel?: Personnel[];
  dealTypes?: DealType[];
  unrulyBundledDeals: UnrulyBundledDeal[];
  currentServerDate: number;
  sspDealList?: SspDealsGroup[];
  safetyFilters: SafetyFilterProvider[];
  dealPartners?: DealPartner[];
}

export interface CachedResponse<T> {
  isFromCache: boolean;
  response: T;
}

export type AggregationFunction = 'SUM' | 'AVERAGE';

type DealExternalSyncActionType = 'SYNC' | 'RETRIEVE';
export type SyncType = 'buyer' | 'marketplace';

export interface DealExternalSync {
  id: number;
  pubDealId: number;
  dspAccountId: number;
  fullName: string;
  synced: boolean;
  syncType?: SyncType;
  syncResult: string;
  externalApiData: {
    id?: string;
    orderId?: string;
    productId?: string;
    dealStatus?: string;
    logoPathS3?: string;
    dv3MarketplaceUri?: string;
    logoUrl?: string;
  };
  dealStatus: { orderStatus: string };
  action: DealExternalSyncActionType;
  updatedAt: string;
  createdAt: string;
}


export interface UnrulyBundledDeal {
  pubDealId: number;
  externalDealId: string;
  description: string;
  status: string;
  dealEnd: string;
}

export interface PubDealBundle {
  pubDealId: number;
}


export interface SspDeals {
  id: number;
}

export interface SentinelResponse<T> {
  data: T;
}

export interface SentinelTemplateValue {
  values: SentinelValues;
  handler?: any;
}

export interface SentinelValues {
  email: string;
  publishers?: (string | number)[];
  publisherId?: (string | number);
  publisherName?: string;
  exchangeDeals?: (string | number)[];
  days?: number;
}

export interface SentinelSubscription {
  templateValue: SentinelTemplateValue;
  ruleId: number;
  userId: string;
}

export interface SentinelSubscriptionResponse {
  id: number;
  rules: SentinelRule;
  templateValue: SentinelTemplateValue;
  userId: string;
  createdAt: Date;
}

export interface SentinelRule {
  id: number;
  name: string;
  interval: string;
  description: string;
  notificationName: string;
  groupName: string;
}

export type ClusivityStatus = 'ACTIVE' | 'INACTIVE';
export type StatusActivity = 'active' | 'inactive';

export enum ActivityStatus {
  ACTIVE = 'ACTIVE',
  INACTIVE = 'INACTIVE',
  ACTIVE_LOWER = 'active',
  INACTIVE_LOWER = 'inactive'
}

export type PadTypeOptions = 'OMP' | 'PMP';

export type PortalType = 'supply' | 'exchange' | 'portal' | 'partner';

export enum PortalTypeEnum {
  SUPPLY = 'supply',
  EXCHANGE = 'exchange',
  PORTAL = 'portal',
  PARTNER = 'partner'
}

export enum PublisherDealPlatformFeeTypeEnum {
  PUB_INITIATED = 'PUB_INITIATED',
  UNRULY_INITIATED = 'UNRULY_INITIATED',
  CUSTOM = 'CUSTOM',
  CUSTOM_UNRULY = 'CUSTOM_UNRULY',
}

export enum PlatformFeeEnum {
  DEMAND_MARGIN = 'demand_margin',
  DEMAND_MARGIN_DATA_FEE = 'demand_margin_data_fee',
  INVENTORY_COST = 'inventory_cost',
  INVENTORY_COST_DATA_FEE = 'inventory_cost_data_fee',
  GROSS_REVENUE = 'gross_revenue'
}

export interface ControlContentAttribute {
  displayName: string;
  value: string;
  isDisabledForRequest: boolean;
  isDisabledForReport: boolean;
}

export interface AudienceTargetingQuery {
  keyword: string;
  provider: string;
  sort: string;
  page?: number;
  size?: number;
}

export interface PublisherListItem {
  publisherId: string;
  publisherName: string;
  disabled?: boolean;
}

export interface PublisherMedia {
  segmentIds: number[];
  page?: number;
  limit?: number;
}

export type PollingConfigResponseEvaluatorOperation = 'EQUALS'|'IS_NOT';

export interface PollingConfig {
  id: string;
  apiMethod: string;
  args?: any[];
  responseEvaluators: {
    success: {
      path: string;
      operation: PollingConfigResponseEvaluatorOperation;
      compareTo: any;
    };
    error?: {
      path: string;
      operation: PollingConfigResponseEvaluatorOperation;
      compareTo: any;
    };
  };
  completionNotification: {
    success: {
      title: string;
      body: string;
    };
    error?: {
      title: string;
      body: string;
    };
    expired?: {
      title: string;
      body: string;
    };
    notificationIdsToRemove?: string[];
  };
  completionEvent?: {
    name: string;
    data?: any;
  };
  interval?: number;
  startedAt?: number;
  maxDurationMs?: number;
}

export interface CloneExchangeDeal {
  valid?: boolean;
  externalDealId?: ExchangeDeal['externalDealId'];
  dealPartnerId?: ExchangeDeal['dealPartnerId'];
  portfolioId?: ExchangeDeal['portfolioId'];
  dealEnd?: ExchangeDeal['dealEnd'];
  dealStart?: ExchangeDeal['dealStart'];
  convertTo?: 'deal' | 'forecast';
  demandMarginOpti?: boolean;
}

export enum UploadFileType {
  TEXT_PLAIN = 'text/plain',
  APPLICATION_VND_MS_EXCEL = 'application/vnd.ms-excel',
  TEXT_X_CSV = 'text/x-csv',
  APPLICATION_CSV = 'application/csv',
  APPLICATION_X_CSV = 'application/x-csv',
  TEXT_CSV = 'text/csv',
  TEXT_COMMA_SEPARATED_VALUES = 'text/comma-separated-values',
  TEXT_X_COMMA_SEPARATED_VALUES = 'text/x-comma-separated-values',
  TEXT_TAB_SEPARATED_VALUES = 'text/tab-separated-values',
  APPLICATION_PDF = 'application/pdf',
  IMAGE_PNG = 'image/png',
  IMAGE_JPEG = 'image/jpeg',
  IMAGE_JPG = 'image/jpg',
}

export interface DealPartnerForm {
  name: FormControl<string | null>;
  platformFee?: FormControl<number | null>;
  platformFeeType?: FormControl<PlatformFeeEnum | null>;
  defaultDemandMargin?: FormControl<number | null>;
  notes?: FormControl<string | null>;
  audienceProviders?: FormControl<number[] | null>;
  status?: FormControl<string | null>;
  createdAt?: FormControl<string | null>;
  partnerType?: FormControl<PartnerType | null>;
  parentPartnerId?: FormControl<number | null>;
}

export interface DayPartingValue {
  days: number[];
  startMinute: number;
  endMinute: number;
}

export interface SupplyPublisherMedia {
  id: number | string;
  name: string;
}

export type MediaMatchingValue = string | SupplyPublisherMedia;

export interface DomainBundleChange {
  value: MediaMatchingValue;
  type: string;
  change: string;
}

export interface ThinPublisherDeal {
  pubDealId: number;
  externalDealId: string;
  description: string;
  status: string;
  dealEnd: string;
}

export interface ExchangePersonnel {
  id?: number;
  name: string;
  role?: string | null;
  email?: string | null;
  employeeId?: string | null;
  salesRegion?: string | null;
  status: string;
}

export interface Region {
  regionId: number;
  region: string;
  name: string;
}

export enum ViewMode {
  VIEW = 'view',
  SELECTION = 'selection',
}

export interface Dv360InstantDealLog {
  id: number;
  orderId: string;
  productId?: string | null;
  etag?: string | null;
  action?: string | null;
  dealId?: number | null;
  forecastId?: number | null;
  isSynced?: boolean | null;
  errorDetails?: Record<string, any> | null;
  createdAt: Date;
  updatedAt: Date;
}

export { ResponseFromServer } from './response-from-server.model';
export { CommonDataModel } from './common-data-model.model';
export { ResponseFromNestServerMeta } from './response-from-nest-server-meta.model';
export { ResponseFromNestServerData } from './response-from-nest-server-data.model';
export { ResponseFromNestServer } from './response-from-nest-server.model';
export { RoleJava, UserRoleType } from './role';
